import { css } from 'lit';

import { nsButtonStyles } from './ns-button-styles.js';
import { nsInputStyles } from './ns-input-styles.js';
import { nsChoiceStyles } from './ns-choice-styles.js';
import { nsMotionKeyframes, nsReducedMotionStyles } from './ns-motion-styles.js';

const utilityClasses = css`
  .m-0 {
    margin: 0;
  }

  .mt-0 {
    margin-top: 0;
  }
  .mt-1 {
    margin-top: var(--wa-space-2xs);
  }
  .mt-2 {
    margin-top: var(--wa-space-xs);
  }
  .mt-3 {
    margin-top: var(--wa-space-m);
  }
  .mt-4 {
    margin-top: var(--wa-space-l);
  }
  .mt-5 {
    margin-top: var(--wa-space-3xl);
  }

  .mr-0 {
    margin-right: 0;
  }
  .mr-1 {
    margin-right: var(--wa-space-2xs);
  }
  .mr-2 {
    margin-right: var(--wa-space-xs);
  }
  .mr-3 {
    margin-right: var(--wa-space-m);
  }
  .mr-4 {
    margin-right: var(--wa-space-l);
  }
  .mr-5 {
    margin-right: var(--wa-space-xl);
  }

  .ml-0 {
    margin-left: 0;
  }
  .ml-1 {
    margin-left: var(--wa-space-2xs);
  }
  .ml-2 {
    margin-left: var(--wa-space-xs);
  }
  .ml-3 {
    margin-left: var(--wa-space-m);
  }
  .ml-4 {
    margin-left: var(--wa-space-l);
  }
  .ml-5 {
    margin-left: var(--wa-space-5xl);
  }

  .mb-0 {
    margin-bottom: 0;
  }
  .mb-1 {
    margin-bottom: var(--wa-space-2xs);
  }
  .mb-2 {
    margin-bottom: var(--wa-space-xs);
  }
  .mb-3 {
    margin-bottom: var(--wa-space-m);
  }
  .mb-4 {
    margin-bottom: var(--wa-space-l);
  }
  .mb-5 {
    margin-bottom: var(--wa-space-3xl);
  }

  .pt-0 {
    padding-top: 0;
  }
  .pt-1 {
    padding-top: var(--wa-space-2xs);
  }
  .pt-2 {
    padding-top: var(--wa-space-xs);
  }
  .pt-3 {
    padding-top: var(--wa-space-m);
  }
  .pt-4 {
    padding-top: var(--wa-space-l);
  }
  .pt-5 {
    padding-top: var(--wa-space-3xl);
  }

  .pr-0 {
    padding-right: 0;
  }
  .pr-1 {
    padding-right: var(--wa-space-2xs);
  }
  .pr-2 {
    padding-right: var(--wa-space-xs);
  }
  .pr-3 {
    padding-right: var(--wa-space-m);
  }
  .pr-4 {
    padding-right: var(--wa-space-l);
  }
  .pr-5 {
    padding-right: var(--wa-space-3xl);
  }

  .pl-0 {
    padding-left: 0;
  }
  .pl-1 {
    padding-left: var(--wa-space-2xs);
  }
  .pl-2 {
    padding-left: var(--wa-space-xs);
  }
  .pl-3 {
    padding-left: var(--wa-space-m);
  }
  .pl-4 {
    padding-left: var(--wa-space-l);
  }
  .pl-5 {
    padding-left: var(--wa-space-3xl);
  }

  button {
    text-transform: none;
    letter-spacing: normal;
  }

  .w-100 {
    width: 100%;
  }

  .h-100 {
    height: 100%;
  }

  .text-center {
    text-align: center;
  }

  .flex {
    flex: 1;
  }
  .vertical {
    display: flex;
    flex-direction: column;
  }
  .horizontal {
    display: flex;
    flex-direction: row;
  }
  .center-center {
    align-items: center;
    justify-content: center;
  }
`;

export const nsSharedStyles = css`
  * {
    box-sizing: border-box;
    scrollbar-width: thin;
    scrollbar-color: var(--ns-scrollbar-thumb-background, var(--ns-theme-type)) transparent;
  }

  [hidden] {
    display: none !important;
  }

  ${utilityClasses}
  ${nsButtonStyles}
  ${nsInputStyles}
  ${nsChoiceStyles}

  /* Set the default params for an element */
  :host {
    font-family: var(--ns-theme-font-family);
    font-weight: 300;
  }

  a.secondary-color {
    color: var(--ns-theme-white);
  }

  a.purplish-blue-300 {
    color: color-mix(in srgb, var(--ns-theme-purple) 20%, var(--ns-theme-white));
  }

  p.underline {
    text-decoration: underline;
  }

  p.mandy {
    color: var(--ns-theme-red);
  }

  p.yellow-tan {
    color: var(--ns-theme-amber);
  }

  .clickable {
    cursor: pointer;
  }

  /*
   * Wrapped in :where() so this reset always has zero specificity — any component-specific
   * class (e.g. .profile, .card, .select-sub) that declares its own background/padding/etc.
   * wins over this fallback instead of being silently overridden by it.
   */
  :where(button.clickable) {
    border: none;
    margin: 0;
    padding: 0;

    font: inherit;
    color: inherit;
    text-align: inherit;

    background: none;
    appearance: none;
  }

  :where(button.card) {
    border: none;

    font: inherit;
    color: inherit;
    text-align: inherit;

    appearance: none;
  }

  .non-clickable {
    cursor: not-allowed;
  }

  .relative-position {
    position: relative;
  }

  .page-not-found {
    color: var(--ns-theme-red);

    font-size: var(--wa-font-size-4xl);
    font-weight: 700;
  }

  [data-title]:after {
    display: flex;
    align-items: center;

    content: attr(data-title);

    position: absolute;
    bottom: 15%;
    left: 120%;

    background-color: var(--ns-theme-type);
    color: var(--ns-theme-white);

    line-height: 1;
    padding: var(--wa-space-2xs);

    font-size: var(--wa-font-size-s);

    white-space: nowrap;

    visibility: hidden;

    z-index: 9999;
  }

  [data-title]:before {
    content: '';

    border-style: solid;
    border-width: 5px 10px 5px 0;
    border-color: transparent var(--ns-theme-type) transparent transparent;

    position: absolute;
    bottom: 12px;
    right: -20%;

    visibility: hidden;
  }

  [data-title]:hover:after,
  [data-title]:hover:before {
    visibility: visible;
  }

  #lottie {
    position: relative;

    width: 200px;

    z-index: 2;
  }

  /* Input fields */
  input {
    background-color: var(--primary-input-background-color);
    color: var(--primary-input-text-color);
    border: none;
    outline: none;

    box-sizing: border-box;

    padding: var(--wa-space-2xs) var(--wa-space-xs);
    border-radius: 2em;

    font-size: 1em;
    line-height: 1em;
  }
  input.secondary {
    background-color: var(--secondary-input-background-color);
    color: var(--secondary-input-text-color);
  }

  textarea {
    resize: vertical;
  }

  input[readonly] {
    border-style: dotted;
    cursor: not-allowed;
    color: var(--ns-theme-grey-dark);
  }

  input[disabled] {
    background-color: var(--ns-theme-grey-mid);
    cursor: not-allowed;
  }

  label {
    font-size: var(--wa-font-size-l);
    font-weight: 500;
    line-height: 1;
  }

  input + label {
    margin-top: var(--wa-space-xl);
  }

  select {
    background-color: var(--primary-input-background-color);
    color: var(--primary-input-text-color);
    border: none;
    outline: none;

    box-sizing: border-box;

    padding: var(--wa-space-2xs);
    border-radius: 2em;

    font-size: 1em;
    line-height: 1em;
  }

  /* TODO: Other fields */

  wa-spinner {
    font-size: var(--wa-font-size-4xl);
    --track-width: 4px;
  }

  .pill {
    display: inline-flex;
    padding: 0.2em 0.5em;
    align-items: center;
    gap: var(--wa-space-xs);
    flex-shrink: 0;
    margin-bottom: var(--wa-space-s);

    border-radius: var(--wa-border-radius-pill);
    background: var(--wa-color-neutral-fill-normal);
    color: var(--wa-color-text-normal);

    font-size: var(--wa-font-size-m);

    cursor: pointer;

    outline: 0;
    border: 0;

    transition:
      background-color var(--ns-motion-duration) ease,
      color var(--ns-motion-duration) ease;
  }
  .pill:hover,
  .pill:focus,
  .pill.active {
    background: var(--wa-color-brand-fill-loud);
    color: var(--wa-color-brand-on-loud);
  }
  .pill__icon {
    width: 1em;
    height: 1em;
  }

  .pill--purple,
  .pill--purple:hover,
  .pill--blue,
  .pill--blue:hover {
    background: var(--wa-color-brand-fill-loud);
    color: var(--wa-color-brand-on-loud);
  }
  .pill--yellow,
  .pill--yellow:hover {
    background: var(--wa-color-warning-fill-loud);
    color: var(--wa-color-warning-on-loud);
  }
  .pill--danger,
  .pill--danger:hover {
    background: var(--wa-color-danger-fill-loud);
    color: var(--wa-color-danger-on-loud);
  }
  .pill--verified,
  .pill--verified:hover {
    background: var(--wa-color-success-fill-loud);
    color: var(--wa-color-success-on-loud);
  }

  .pill--x2 {
    font-size: var(--wa-font-size-xl);
  }

  .pill--outline {
    background: transparent;
    color: var(--wa-color-text-normal);
    border: 1px solid var(--wa-color-neutral-border-loud);
  }
  .pill--outline:hover,
  .pill--outline:focus,
  .pill--outline.active {
    background: transparent;
    color: var(--wa-color-brand-on-quiet);
    border: 1px solid var(--wa-color-brand-border-loud);
  }

  .blocks {
    display: flex;
    flex-direction: row;
    gap: var(--wa-space-m);
    margin-bottom: var(--wa-space-m);
  }
  .blocks .block {
    display: flex;
    flex-direction: column;
    flex: 1;

    background-color: var(--secondary-background-color);
    color: var(--secondary-text-color);

    min-height: 100px;

    padding: var(--wa-space-xs);
  }
  .blocks .block__title {
    font-size: var(--wa-font-size-m);
    font-weight: 500;
    line-height: 1;
    margin-bottom: var(--wa-space-xs);
  }
  .blocks .block__body {
    flex: 1;
  }
  .blocks .block__buttons {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: var(--wa-space-xs);
  }

  .stats {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    align-items: center;
    justify-content: center;

    margin: 0 -4px;
  }
  .stat {
    background-color: var(--ns-theme-background);
    color: var(--ns-theme-type);
    border-radius: var(--wa-border-radius-m);
    padding: var(--wa-space-xs);
    flex: 1 0 125px;

    margin: 0px var(--wa-space-2xs) var(--wa-space-xs);
  }
  /* Status is a fill behind the count; #d2ffdf and #ffe56c can't be read as text on the light tile */
  .stat--green .stat__number,
  .stat--amber .stat__number,
  .stat--red .stat__number {
    display: inline-block;
    min-width: 1.4em;
    padding: 0 var(--wa-space-xs);
    border-radius: var(--wa-border-radius-pill);
    text-align: center;
  }
  .stat--green .stat__number {
    background: var(--ns-theme-green);
  }
  .stat--amber .stat__number {
    background: var(--ns-theme-amber);
  }
  .stat--red .stat__number {
    background: var(--ns-theme-red);
    color: var(--ns-theme-black);
  }

  .stat__label {
    font-size: 1em;
    line-height: 1;
  }
  .stat__number {
    font-size: 3.5em;
    line-height: 1;
  }

  .message {
    border-radius: 2em;
    padding: var(--wa-space-3xs) 1em;
    margin-bottom: var(--wa-space-2xs);
  }
  .message--success {
    background-color: var(--ns-theme-green);
    color: var(--ns-theme-type);
  }
  .message--info {
    background-color: var(--ns-theme-purple);
    color: var(--ns-theme-white);
  }
  .message--warning {
    background-color: var(--ns-theme-amber);
    color: var(--ns-theme-type);
  }
  .message--error {
    background-color: var(--ns-theme-red);
    color: var(--ns-theme-black);
  }

  .filter-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;

    cursor: pointer;

    aspect-ratio: 1;
    border: none;
    background: var(--ns-search-background, var(--wa-color-surface-default));
    color: var(--wa-color-text-normal);

    border-radius: var(--wa-border-radius-circle);
    padding: var(--wa-space-2xs);
  }

  button[disabled] {
    cursor: not-allowed;
  }

  .divider {
    height: 1px;
    width: 100%;

    background-color: var(--primary-color);

    border-radius: var(--wa-border-radius-l);
  }

  .section-rule {
    border-top: 1px solid var(--ns-theme-white);
    padding-top: var(--wa-space-m);
  }

  .form-error {
    font-size: var(--wa-font-size-m);
  }

  .form-error,
  .error-text {
    color: var(--ns-error-text-color, var(--ns-theme-red));
  }

  .toggle {
    background-color: var(--primary-card-background-color);

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-items: center;

    border-radius: var(--wa-border-radius-circle);

    transform: rotate(0deg);
    transition: transform 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  }

  .toggle.rotate {
    transform: rotate(180deg);
    transition: transform 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  }

  .white-text {
    color: var(--primary-text-color);
  }

  .confirm-btn {
    position: relative;
    width: 70px;
    height: 25px;

    background-color: var(--primary-accent-color);
    color: var(--secondary-text-color);
    border: 0;
    border-radius: var(--wa-border-radius-l);

    text-transform: none;
    font-size: var(--wa-font-size-xs);

    cursor: pointer;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .text {
    display: block;
    transition: opacity 0.2s;
  }

  .tick-icon {
    position: absolute;
    left: 50%;
    top: 50%;

    transform: translate(-50%, -50%) scale(0);
    font-size: var(--wa-font-size-xs);
    transition: transform 0.3s ease;
  }

  .confirm-btn.ticked {
    width: 25px;
    border-radius: var(--wa-border-radius-circle);
    background-color: var(--primary-accent-color);
    cursor: auto;
  }

  .confirm-btn.ticked .text {
    opacity: 0;
  }

  .confirm-btn.ticked .tick-icon {
    transform: translate(-50%, -50%) scale(1);
  }

  h2 {
    margin: 0;
    font-weight: 300;
  }

  .mt-auto {
    margin-top: auto;
  }

  .large {
    font-size: var(--wa-font-size-xl);
  }

  .medium {
    font-size: var(--wa-font-size-m);
  }
  .small {
    font-size: var(--wa-font-size-xs);
  }
  .extra-small {
    font-size: var(--wa-font-size-3xs);
  }

  span.underline {
    text-decoration: underline;
  }

  wa-button .green-dot {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    background: var(--ns-theme-green);
    width: 20px;
    height: 20px;
    padding: 0;
    border-radius: var(--wa-border-radius-circle);
    color: var(--ns-theme-type);
  }

  wa-button .green-dot img,
  wa-button .green-dot ns-icon {
    --icon-width: 55%;
    --icon-height: 55%;
    width: 55%;
    height: 55%;
  }

  .max-content {
    width: max-content;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .fieldset {
    border: none;
    margin: 0;
    padding: 0;
    min-width: 0;
  }

  /* Chrome, Edge and Safari */
  *::-webkit-scrollbar {
    height: 16px;
    width: 16px;
  }

  *::-webkit-scrollbar-track {
    background-color: transparent;
  }

  *::-webkit-scrollbar-thumb {
    border: 4px solid transparent;
    border-radius: var(--wa-border-radius-m);
    background-clip: padding-box;
    background-color: var(--ns-scrollbar-thumb-background, var(--ns-theme-type));
  }

  *::-webkit-scrollbar-thumb:hover,
  *::-webkit-scrollbar-thumb:active {
    background-color: var(--ns-scrollbar-thumb-background-hover, var(--ns-theme-type));
  }

  /* Small */
  @media (max-width: 768px) {
    .ml-5 {
      margin: 0;
    }
  }

  /* X-small */
  @media (max-width: 576px) {
    p,
    strong {
      font-size: var(--wa-font-size-xs);
    }
  }

  ${nsMotionKeyframes}

  .tab-panels > *,
  .tab-panel {
    animation: ns-fade-in var(--ns-motion-duration) var(--ns-motion-easing);
  }

  ${nsReducedMotionStyles}
`;
